<template>
    <VAutocomplete
        v-model="displayValue"
        v-bind="props.fieldProps"
        :label="computedLabel"
        :items="props.items"
        :multiple="props.multiple"
        :chips="props.chips"
        :closable-chips="props.chips && props.multiple"
        :clearable="props.clearable"
        :item-title="props.itemTitle"
        :item-value="props.itemValue"
        :loading="props.loading"
        :required="props.required"
        :item-disabled="props.itemDisabled"
        :return-object="props.returnObject"
        :density="density"
        v-model:search="searchText"
        variant="outlined"
        rounded
        autocomplete="off"
        @focus="onFocus"
    >
        <template
            v-if="props.colorByMemberId && props.chips && props.multiple"
            #chip="{ item, props: chipProps }"
        >
            <AQChip
                v-bind="chipProps"
                size="small"
                variant="flat"
                closable
                :class="getMemberChipClass(item)"
                :text="item.title"
            />
        </template>
        <template v-if="props.enableInfinite && props.items.length && !searchText" #append-item>
            <VInfiniteScroll mode="intersect" @load="debouncedEmitLoadMore">
                <template #empty>
                    <span>{{ $t('pagination.noMoreData') }}</span>
                </template>
            </VInfiniteScroll>
        </template>
    </VAutocomplete>
</template>

<script setup lang="ts">
    import { computed, ref, watch, nextTick } from 'vue';
    import { VAutocomplete, VInfiniteScroll } from 'vuetify/components';
    import { debounce } from 'lodash';

    import AQChip from '@/components/shared/interactions/AQChip.vue';
    import { LoadMoreOptions } from '@/interfaces/common';

    type CommonProps = {
        items?: object[];
        fieldProps?: object;
        label?: string;
        color?: string;
        chips?: boolean;
        clearable?: boolean;
        multiple?: boolean;
        modelValue?: unknown;
        itemTitle?: string | ((item: object) => string);
        itemValue?: string | ((item: object) => string | number);
        loading?: boolean;
        itemDisabled?: boolean;
        enableInfinite?: boolean;
        returnObject?: boolean;
        required?: boolean;
        density?: 'default' | 'compact' | 'comfortable';
        colorByMemberId?: boolean;
    };

    const props = withDefaults(defineProps<CommonProps>(), {
        fieldProps: () => ({}),
        items: () => [],
        chips: false,
        clearable: false,
        multiple: false,
        modelValue: undefined,
        color: undefined,
        itemTitle: 'name',
        itemValue: 'id',
        itemDisabled: false,
        enableInfinite: false,
        returnObject: false,
        density: 'default',
        colorByMemberId: false,
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', v: string | string[]): void;
        (e: 'change', v: unknown): void;
        (e: 'load-more', options: LoadMoreOptions): void;
        (e: 'update:search', search: string): void;
    }>();

    const innerValue = computed({
        get: () => props.modelValue,
        set: (val: string | string[]) => emit('update:modelValue', val),
    });

    const displayValue = computed({
        get: () => {
            if (!props.items.length) {
                return props.multiple ? [] : null;
            }
            return props.modelValue;
        },
        set: (val: string | string[]) => emit('update:modelValue', val),
    });

    const computedLabel = computed(() => {
        if (props.required) {
            return `${props.label} *`;
        }
        return props.label;
    });

    const searchText = ref('');
    const debouncedEmitLoadMore = debounce((options: LoadMoreOptions) => {
        if (searchText.value) {
            options.done && options.done('empty');
            return;
        }

        if (!props.items.length) {
            options.done && options.done('empty');
            return;
        }

        emit('load-more', options);
    }, 500);

    const getItemTitle = (item: object): string => {
        if (typeof props.itemTitle === 'function') {
            return props.itemTitle(item);
        }
        return (item as Record<string, unknown>)[props.itemTitle as string] as string;
    };

    watch(innerValue, (newValue: unknown) => {
        emit('change', newValue);
    });

    const debouncedEmitSearch = debounce((value: string) => {
        emit('update:search', value);
    }, 500);

    watch(searchText, (newSearch: string) => {
        // Check if search matches any item title (indicates selection, not typing)
        const isSelection = props.items.some((item) => getItemTitle(item) === newSearch);

        if (!isSelection) {
            debouncedEmitSearch(newSearch);
        }
    });

    const getMemberChipClass = (item: { raw?: unknown }): string => {
        return (item.raw as Record<string, unknown>)?.memberId ? 'recipient-chip--member' : '';
    };

    const onFocus = (e: FocusEvent) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        const input = e.target;
        nextTick(() => input.select());
    };
</script>
